"use client";

/**
 * Wide @container arrow / chevron layout for horizontal Stepper.
 * Circles remain the narrow / vertical SoT; this strip mounts at @[40rem]+.
 *
 * Borders use clip-path + drop-shadow so the stroke follows chevron edges
 * (rectangular CSS borders cannot).
 */

import type { ReactNode, SVGProps } from "react";
import { cn } from "../../lib";
import {
	SQ_GREEN_BAR_STRIPE_CLASS,
	SQ_GREEN_LINEAR_GRADIENT_CLASS,
} from "../../lib/sq-color-runtime-exports";
import { IntentProgressBar } from "../intent-ui/progress-bar";

/** Matches circle Stepper indicator visual states. */
export type StepperArrowIndicatorStatus =
	| "active"
	| "opened"
	| "inactive"
	| "complete"
	| "skipped";

export interface StepperArrowStripStep {
	id: string;
	title: string;
	/** Supporting line under the title (e.g. "3 tasks"). */
	subtext?: string;
	status: StepperArrowIndicatorStatus;
	progressPercent?: number | null;
	requiredComplete?: boolean;
	/** When true, segment is non-interactive (parity with circle `blocked`). */
	blocked?: boolean;
}

export interface StepperArrowStripProps {
	steps: StepperArrowStripStep[];
	ariaLabel?: string;
	className?: string;
	disableStepIndicators?: boolean;
	dimDisabledIndicators?: boolean;
	onStepClick?: (stepId: string) => void;
}

/** Chevron depth — also used as negative overlap between segments. */
const CHEVRON_PX = 16;

/** Matches header HUD / PA step-footer EXP track. */
const EXP_TRACK_CLASS = "h-1.5 rounded-full bg-black/10 dark:bg-white/15";

/**
 * Mirrors `SECONDARY_CTA_GRADIENT_SURFACE` from apps/web CTA helpers
 * (ui package cannot import app paths).
 */
const SECONDARY_CTA_SURFACE_CLASS = cn(
	"bg-gradient-to-b from-white via-neutral-50 to-neutral-100 text-neutral-900",
	"dark:from-[#243652] dark:via-[#1a2435] dark:to-[#0f1419] dark:text-white",
);

function clampProgressPercent(value: number): number {
	return Math.max(0, Math.min(100, value));
}

function chevronClipPath(
	position: "first" | "middle" | "last" | "only",
): string | undefined {
	const n = `${CHEVRON_PX}px`;
	switch (position) {
		case "only":
			return undefined;
		case "first":
			return `polygon(0% 0%, calc(100% - ${n}) 0%, 100% 50%, calc(100% - ${n}) 100%, 0% 100%)`;
		case "last":
			return `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, ${n} 50%)`;
		case "middle":
			return `polygon(0% 0%, calc(100% - ${n}) 0%, 100% 50%, calc(100% - ${n}) 100%, 0% 100%, ${n} 50%)`;
	}
}

function segmentPosition(
	index: number,
	total: number,
): "first" | "middle" | "last" | "only" {
	if (total <= 1) {
		return "only";
	}
	if (index === 0) {
		return "first";
	}
	if (index === total - 1) {
		return "last";
	}
	return "middle";
}

/** Stroke that follows clip-path outline (unlike box-shadow / CSS border).
 * Always present for segment separation — stronger for active/complete. */
function chevronBorderFilter(status: StepperArrowIndicatorStatus): string {
	const baseSeparation =
		"drop-shadow(0 0 0 1px color-mix(in srgb, var(--border) 90%, var(--foreground)))";
	if (status === "complete" || status === "skipped" || status === "active") {
		return [
			baseSeparation,
			"drop-shadow(0 0 0 1.5px var(--sq-green-static-from))",
			"drop-shadow(1px 0 0 var(--sq-green-static-to))",
			"drop-shadow(-1px 0 0 var(--sq-green-static-from))",
		].join(" ");
	}
	if (status === "opened") {
		return [
			baseSeparation,
			"drop-shadow(0 0 0 1.5px color-mix(in srgb, var(--border) 55%, var(--foreground)))",
			"drop-shadow(0 1px 2px rgb(15 23 42 / 0.08))",
		].join(" ");
	}
	return baseSeparation;
}

function softFillClass(status: StepperArrowIndicatorStatus): string {
	if (status === "opened") {
		return SECONDARY_CTA_SURFACE_CLASS;
	}
	if (status === "active") {
		return "bg-[color-mix(in_srgb,var(--sq-green-static-from)_18%,var(--background))]";
	}
	if (status === "complete" || status === "skipped") {
		return "bg-[color-mix(in_srgb,var(--sq-green-static-from)_12%,var(--background))]";
	}
	return "bg-muted/40 dark:bg-muted/25";
}

function contentPadClass(
	position: "first" | "middle" | "last" | "only",
): string {
	switch (position) {
		case "only":
			return "px-3";
		case "first":
			return "pl-3 pr-5";
		case "last":
			return "pl-5 pr-3";
		case "middle":
			return "pl-5 pr-5";
	}
}

function shouldShowProgress(
	status: StepperArrowIndicatorStatus,
	progressPercent: number | null | undefined,
	requiredComplete: boolean,
): { show: boolean; value: number } {
	const hasProgressProp =
		typeof progressPercent === "number" && Number.isFinite(progressPercent);
	if (!hasProgressProp) {
		return { show: false, value: 0 };
	}
	const clamped = clampProgressPercent(progressPercent);
	const allTasksDone = clamped >= 100;
	const isSelected = status === "opened" || status === "active";
	const show =
		!allTasksDone && (isSelected || (!requiredComplete && clamped > 0));
	return { show, value: clamped };
}

function ArrowCheckIcon(props: SVGProps<SVGSVGElement>) {
	return (
		<svg
			{...props}
			fill="none"
			stroke="currentColor"
			strokeWidth={2.5}
			viewBox="0 0 24 24"
			aria-hidden="true"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M5 13l4 4L19 7"
			/>
		</svg>
	);
}

function StepNumberTile({
	step,
	status,
	showIcons,
}: {
	step: number;
	status: StepperArrowIndicatorStatus;
	showIcons: boolean;
}) {
	const treatAsComplete =
		status !== "opened" && (status === "complete" || status === "skipped");
	const isLive = status === "active" || status === "opened";

	let overlay: ReactNode = null;
	if (showIcons && isLive) {
		overlay = (
			<span
				className={cn(
					"absolute -top-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full ring-2 ring-background",
					status === "opened"
						? "bg-neutral-900 dark:bg-white"
						: cn(SQ_GREEN_LINEAR_GRADIENT_CLASS, "text-white"),
				)}
				aria-hidden
			>
				<span
					className={cn(
						"size-1.5 rounded-full",
						status === "opened"
							? "bg-white dark:bg-neutral-900"
							: "bg-white",
					)}
				/>
			</span>
		);
	} else if (showIcons && treatAsComplete) {
		overlay = (
			<span
				className={cn(
					"absolute -top-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full text-white ring-2 ring-background",
					SQ_GREEN_LINEAR_GRADIENT_CLASS,
				)}
				aria-hidden
			>
				<ArrowCheckIcon className="size-2.5" />
			</span>
		);
	}

	return (
		<span
			className={cn(
				"relative flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted/30",
				"font-black text-xl leading-none tabular-nums text-muted-foreground/50",
			)}
			aria-hidden
		>
			{step}
			{overlay}
		</span>
	);
}

function ArrowStepSegment({
	step,
	index,
	total,
	disableStepIndicators,
	dimDisabledIndicators,
	onStepClick,
}: {
	step: StepperArrowStripStep;
	index: number;
	total: number;
	disableStepIndicators: boolean;
	dimDisabledIndicators: boolean;
	onStepClick?: (stepId: string) => void;
}) {
	const position = segmentPosition(index, total);
	const clipPath = chevronClipPath(position);
	const stepDisabled = disableStepIndicators || step.blocked === true;
	const progress = shouldShowProgress(
		step.status,
		step.progressPercent,
		step.requiredComplete === true,
	);

	function handleClick() {
		if (stepDisabled) {
			return;
		}
		onStepClick?.(step.id);
	}

	const shell = (
		<div
			className={cn(
				"flex h-full min-h-[4.25rem] w-full flex-col justify-center gap-1.5 py-0",
				softFillClass(step.status),
				contentPadClass(position),
				position === "only" && "rounded-xl",
			)}
			style={{
				...(clipPath ? { clipPath } : {}),
				filter: chevronBorderFilter(step.status),
			}}
		>
			<div className="flex min-w-0 items-center gap-2.5">
				<StepNumberTile
					step={index + 1}
					status={step.status}
					showIcons
				/>
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-semibold leading-tight text-foreground">
						{step.title}
					</p>
					{step.subtext ? (
						<p className="mt-0.5 truncate text-[10px] leading-snug text-muted-foreground">
							{step.subtext}
						</p>
					) : null}
				</div>
			</div>
			{progress.show ? (
				<IntentProgressBar
					value={progress.value}
					className="relative z-0 flex h-1.5 min-h-1.5 w-full items-center"
					trackClassName={EXP_TRACK_CLASS}
					indicatorClassName={SQ_GREEN_BAR_STRIPE_CLASS}
					aria-label={`${step.title} progress ${progress.value}%`}
					data-testid={`stepper-arrow-progress-${step.id}`}
				/>
			) : (
				<span className="h-1.5" aria-hidden />
			)}
		</div>
	);

	return (
		<div
			className={cn(
				"relative min-w-[8.5rem] flex-1",
				stepDisabled
					? dimDisabledIndicators || step.blocked
						? "cursor-not-allowed opacity-50"
						: "cursor-default"
					: "cursor-pointer",
			)}
			style={{
				zIndex: total - index,
				marginLeft: index === 0 ? 0 : -CHEVRON_PX,
			}}
			data-stepper-step={step.id}
			data-stepper-status={step.status}
			data-stepper-layout="arrow"
		>
			{stepDisabled ? (
				<div
					className="h-full w-full"
					aria-current={
						step.status === "active" || step.status === "opened"
							? "step"
							: undefined
					}
				>
					{shell}
				</div>
			) : (
				<button
					type="button"
					className="h-full w-full appearance-none border-0 bg-transparent p-0 text-left outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
					aria-current={
						step.status === "active" || step.status === "opened"
							? "step"
							: undefined
					}
					onClick={handleClick}
				>
					{shell}
				</button>
			)}
		</div>
	);
}

/**
 * Interlocking chevron step strip for wide horizontal layouts.
 * Parent Stepper should mount this only when orientation is horizontal.
 */
export function StepperArrowStrip({
	steps,
	ariaLabel: _ariaLabel = "Progress steps",
	className,
	disableStepIndicators = false,
	dimDisabledIndicators = true,
	onStepClick,
}: StepperArrowStripProps) {
	const actionable = steps.filter((step) => step.id.length > 0);

	return (
		<div
			className={cn(
				// Segments keep a readable min width; parent Stepper scrolls
				// when the strip exceeds the available width.
				"flex w-max min-w-full items-stretch",
				className,
			)}
			data-testid="react-bits-stepper-arrow-strip"
			data-stepper-layout="arrows"
		>
			{actionable.map((step, index) => (
				<ArrowStepSegment
					key={step.id}
					step={step}
					index={index}
					total={actionable.length}
					disableStepIndicators={disableStepIndicators}
					dimDisabledIndicators={dimDisabledIndicators}
					onStepClick={onStepClick}
				/>
			))}
		</div>
	);
}
